import { create } from 'zustand';
import { simulationAPI } from '../services/simulationApi';

const useSimulationStore = create((set, get) => ({
  // State
  isSimulating: false,
  simulationState: null,
  chaosResults: null,
  spofAnalysis: null,
  scenarios: [],
  error: null,

  // Animation state
  isAnimating: false,
  animationSpeed: 1,
  currentStep: 0,

  // Configuration
  config: {
    entryTraffic: {},
    readWriteRatio: 0.8,
    trafficPattern: 'steady',
    spikeFactor: 1.0,
    maxSteps: 100,
    enableAnimation: true,
  },

  // Edge traffic type overrides (user-defined)
  edgeConfigs: {},

  // Selected node for SPOF testing
  selectedNodeForSpof: null,

  // Actions
  setConfig: (newConfig) => {
    set((state) => ({
      config: { ...state.config, ...newConfig },
    }));
  },

  setEdgeConfig: (edgeId, config) => {
    set((state) => ({
      edgeConfigs: {
        ...state.edgeConfigs,
        [edgeId]: config,
      },
    }));
  },

  clearEdgeConfigs: () => {
    set({ edgeConfigs: {} });
  },

  // Run traffic simulation
  runSimulation: async (designJson, componentList) => {
    const { config, edgeConfigs } = get();

    set({ isSimulating: true, error: null });

    try {
      const response = await simulationAPI.runSimulation(
        designJson,
        componentList,
        config,
        Object.keys(edgeConfigs).length > 0 ? edgeConfigs : null
      );

      set({
        simulationState: response.data.state,
        isSimulating: false,
        currentStep: 0,
      });

      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Simulation failed',
        isSimulating: false,
      });
      throw error;
    }
  },

  // Run chaos simulation
  runChaosSimulation: async (designJson, componentList, options = {}) => {
    set({ isSimulating: true, error: null });

    try {
      const response = await simulationAPI.runChaos(designJson, componentList, options);

      set({
        chaosResults: response.data.chaos_results || [],
        spofAnalysis: response.data.spof_analysis || [],
        isSimulating: false,
      });

      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Chaos simulation failed',
        isSimulating: false,
      });
      throw error;
    }
  },

  // Test all SPOFs
  testAllSpofs: async (designJson, componentList) => {
    return get().runChaosSimulation(designJson, componentList, { testAllSpofs: true });
  },

  // Test single node SPOF
  testNodeSpof: async (designJson, componentList, nodeId) => {
    set({ isSimulating: true, error: null, selectedNodeForSpof: nodeId });

    try {
      const response = await simulationAPI.testSingleSpof(designJson, componentList, nodeId);

      set({ isSimulating: false });
      return response.data.analysis;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'SPOF test failed',
        isSimulating: false,
      });
      throw error;
    }
  },

  // Fetch available scenarios
  fetchScenarios: async () => {
    try {
      const response = await simulationAPI.getScenarios();
      set({ scenarios: response.data.scenarios || [] });
      return response.data.scenarios;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch scenarios' });
      throw error;
    }
  },

  // Run specific scenario
  runScenario: async (designJson, componentList, scenarioId) => {
    return get().runChaosSimulation(designJson, componentList, { scenarioId });
  },

  // Animation controls
  startAnimation: () => {
    set({ isAnimating: true });
  },

  stopAnimation: () => {
    set({ isAnimating: false });
  },

  setAnimationSpeed: (speed) => {
    set({ animationSpeed: speed });
  },

  setCurrentStep: (step) => {
    set({ currentStep: step });
  },

  nextStep: () => {
    const { simulationState, currentStep } = get();
    if (simulationState?.steps && currentStep < simulationState.steps.length - 1) {
      set({ currentStep: currentStep + 1 });
    }
  },

  prevStep: () => {
    const { currentStep } = get();
    if (currentStep > 0) {
      set({ currentStep: currentStep - 1 });
    }
  },

  resetAnimation: () => {
    set({ currentStep: 0, isAnimating: false });
  },

  // Clear results
  clearResults: () => {
    set({
      simulationState: null,
      chaosResults: null,
      spofAnalysis: null,
      error: null,
      currentStep: 0,
      isAnimating: false,
    });
  },

  // Reset store
  reset: () => {
    set({
      isSimulating: false,
      simulationState: null,
      chaosResults: null,
      spofAnalysis: null,
      scenarios: [],
      error: null,
      isAnimating: false,
      animationSpeed: 1,
      currentStep: 0,
      config: {
        entryTraffic: {},
        readWriteRatio: 0.8,
        trafficPattern: 'steady',
        spikeFactor: 1.0,
        maxSteps: 100,
        enableAnimation: true,
      },
      edgeConfigs: {},
      selectedNodeForSpof: null,
    });
  },
}));

export default useSimulationStore;
